import { RiLoader4Line } from "@remixicon/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useState } from "react";
import { toast } from "sonner";
import { fetchClient } from "@/api/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";

interface IngestReport {
	id: string;
	filename: string;
	reportPeriodStart?: string | null;
	reportPeriodEnd?: string | null;
	successCount: number;
	warningCount: number;
	errorCount: number;
	lineCount: number;
	ingestedAt: string;
}

interface IngestResultLine {
	sku?: string | null;
	barcode?: string | null;
	transactionType?: string | null;
	resultType: string;
	resultCode?: string | null;
	resultDesc?: string | null;
}

interface IngestReportDetail {
	report: IngestReport;
	lines: IngestResultLine[];
}

const LIST_QUERY_KEY = ["farfetch", "catalogue-results"] as const;
const CURRENT_QUERY_KEY = ["farfetch", "catalogue-results", "current"] as const;

interface BarcodeVerdictSummary {
	published: number;
	success: number;
	warning: number;
	error: number;
	unknown: number;
	topErrorCodes: { resultCode?: string | null; count: number }[];
	latestReportPeriodEnd?: string | null;
}

/// The per-barcode state of the published catalogue as Farfetch LAST reported
/// it. The report list below is per upload and a delta, so it can say "ever
/// rejected" but not "rejected now" - this line is the current number.
function CurrentVerdicts({ summary }: { summary: BarcodeVerdictSummary }) {
	const top = summary.topErrorCodes[0];
	return (
		<div
			data-testid="current-verdicts"
			className="flex flex-wrap items-center gap-2 rounded-md border bg-muted/30 px-3 py-2 text-sm"
		>
			<span className="text-muted-foreground">Published barcodes, latest Farfetch verdict:</span>
			<Badge variant="secondary">{summary.published} published</Badge>
			<Badge variant="secondary">{summary.success} accepted</Badge>
			<Badge variant={summary.warning > 0 ? "outline" : "secondary"}>
				{summary.warning} warned
			</Badge>
			<Badge variant={summary.error > 0 ? "destructive" : "secondary"}>
				{summary.error} rejected
			</Badge>
			<Badge variant="outline">{summary.unknown} never reported</Badge>
			{top && summary.error > 0 && (
				<span className="text-xs text-muted-foreground">
					most common: {top.resultCode ?? "(no code)"} ({top.count})
				</span>
			)}
		</div>
	);
}

function formatPeriod(report: IngestReport): string {
	if (!report.reportPeriodStart) return new Date(report.ingestedAt).toLocaleString();
	const start = new Date(report.reportPeriodStart).toLocaleDateString();
	if (!report.reportPeriodEnd) return start;
	const end = new Date(report.reportPeriodEnd).toLocaleDateString();
	return start === end ? start : `${start} - ${end}`;
}

/// Catalogue results - the operator-facing view of Farfetch's post-upload
/// ingest reports (success/warning/error line counts per submitted CSV).
/// Each row expands to fetch its non-SUCCESS lines, so operators can see
/// exactly which SKUs Farfetch rejected or flagged without opening the raw
/// report file.
export function CatalogueResults({ initialReportId }: { initialReportId?: string }) {
	const qc = useQueryClient();
	// Seeded from the deep link (a notification names one report), so the
	// operator lands on the rejected lines instead of an unexpanded list.
	const [expandedId, setExpandedId] = useState<string | null>(initialReportId ?? null);

	const query = useQuery({
		queryKey: LIST_QUERY_KEY,
		queryFn: async () => {
			const { data, error } = await fetchClient.GET("/api/v1/farfetch/catalogue-results" as never);
			if (error) throw new Error("failed to load catalogue results");
			return (data as { data: IngestReport[] }).data;
		},
	});

	const currentQuery = useQuery({
		queryKey: CURRENT_QUERY_KEY,
		queryFn: async () => {
			const { data, error } = await fetchClient.GET(
				"/api/v1/farfetch/catalogue-results/current" as never,
			);
			if (error) throw new Error("failed to load current verdicts");
			return data as BarcodeVerdictSummary;
		},
	});

	const detailQuery = useQuery({
		queryKey: ["farfetch", "catalogue-results", expandedId],
		queryFn: async () => {
			const { data, error } = await fetchClient.GET(
				`/api/v1/farfetch/catalogue-results/${expandedId}` as never,
			);
			if (error) throw new Error("failed to load report detail");
			return data as IngestReportDetail;
		},
		enabled: expandedId !== null,
	});

	const pollMutation = useMutation({
		mutationFn: async () => {
			const { data, error } = await fetchClient.POST(
				"/api/v1/farfetch/catalogue-results/poll-now" as never,
			);
			if (error) {
				const e = error as { detail?: string; error?: string; message?: string };
				const msg = e.detail ?? e.message ?? e.error ?? "";
				if (/already running/i.test(msg)) {
					throw new Error("a poll is already running");
				}
				throw new Error(msg || "Checking for results failed");
			}
			return data as { reportsIngested: number };
		},
		onSuccess: (result) => {
			qc.invalidateQueries({ queryKey: LIST_QUERY_KEY });
			qc.invalidateQueries({ queryKey: CURRENT_QUERY_KEY });
			toast.success(
				result.reportsIngested > 0
					? `Ingested ${result.reportsIngested} new report(s).`
					: "Checked - no new reports found.",
			);
		},
		onError: (e: Error) => toast.error(e.message),
	});

	function toggleExpand(id: string) {
		setExpandedId((prev) => (prev === id ? null : id));
	}

	if (query.isLoading) {
		return (
			<div className="space-y-2">
				{[0, 1, 2].map((i) => (
					<Skeleton key={i} className="h-9 w-full" />
				))}
			</div>
		);
	}

	if (query.isError) {
		return (
			<div
				role="alert"
				className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm space-y-2"
			>
				<p className="font-medium">Failed to load catalogue results.</p>
				<Button type="button" variant="outline" size="sm" onClick={() => query.refetch()}>
					Retry
				</Button>
			</div>
		);
	}

	const reports = query.data ?? [];

	return (
		<div className="space-y-3">
			<div className="flex items-center justify-between">
				<p className="text-sm text-muted-foreground">
					Post-upload ingest reports from Farfetch (success / warning / error counts per CSV).
				</p>
				<Button
					type="button"
					variant="outline"
					size="sm"
					disabled={pollMutation.isPending}
					onClick={() => pollMutation.mutate()}
				>
					{pollMutation.isPending && <RiLoader4Line className="mr-1 size-4 animate-spin" />}
					Check results now
				</Button>
			</div>

			{currentQuery.data && <CurrentVerdicts summary={currentQuery.data} />}

			{reports.length === 0 ? (
				<p className="text-sm text-muted-foreground">
					No catalogue results yet - reports appear here after Farfetch processes an uploaded CSV.
				</p>
			) : (
				<div className="rounded-md border">
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Report</TableHead>
								<TableHead className="text-right w-20">Success</TableHead>
								<TableHead className="text-right w-20">Warning</TableHead>
								<TableHead className="text-right w-20">Error</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{reports.map((r) => (
								<Fragment key={r.id}>
									<TableRow className="cursor-pointer" onClick={() => toggleExpand(r.id)}>
										<TableCell className="font-medium">
											<div>{r.filename}</div>
											<div className="text-xs text-muted-foreground">{formatPeriod(r)}</div>
										</TableCell>
										<TableCell className="text-right">
											<Badge variant="secondary">{r.successCount}</Badge>
										</TableCell>
										<TableCell className="text-right">
											<Badge variant={r.warningCount > 0 ? "outline" : "secondary"}>
												{r.warningCount}
											</Badge>
										</TableCell>
										<TableCell className="text-right">
											<Badge variant={r.errorCount > 0 ? "destructive" : "secondary"}>
												{r.errorCount}
											</Badge>
										</TableCell>
									</TableRow>
									{expandedId === r.id && (
										<TableRow>
											<TableCell colSpan={4} className="bg-muted/30">
												{detailQuery.isLoading ? (
													<Skeleton className="h-9 w-full" />
												) : (detailQuery.data?.lines.length ?? 0) === 0 ? (
													<p className="text-sm text-muted-foreground py-1">
														No warning or error lines in this report.
													</p>
												) : (
													<Table>
														<TableHeader>
															<TableRow>
																<TableHead>SKU</TableHead>
																<TableHead>Barcode</TableHead>
																<TableHead>Type</TableHead>
																<TableHead>Description</TableHead>
															</TableRow>
														</TableHeader>
														<TableBody>
															{detailQuery.data?.lines.map((line) => (
																<TableRow
																	key={`${line.sku ?? ""}-${line.barcode ?? ""}-${line.resultCode ?? ""}-${line.resultDesc ?? ""}`}
																>
																	<TableCell>{line.sku ?? "-"}</TableCell>
																	<TableCell>{line.barcode ?? "-"}</TableCell>
																	<TableCell>
																		<Badge
																			variant={
																				line.resultType === "ERROR" ? "destructive" : "outline"
																			}
																		>
																			{line.resultType}
																		</Badge>
																	</TableCell>
																	<TableCell>{line.resultDesc ?? "-"}</TableCell>
																</TableRow>
															))}
														</TableBody>
													</Table>
												)}
											</TableCell>
										</TableRow>
									)}
								</Fragment>
							))}
						</TableBody>
					</Table>
				</div>
			)}
		</div>
	);
}
